<script>
/* global axios */
import QRCode from 'qrcode';
import { useAlert } from 'dashboard/composables';

// Caixa WhatsApp HÍBRIDA (base-Cloud): dentro da PRÓPRIA caixa Cloud oficial você
// lê o QR e conecta a perna "sem cobrança" (AstraCall). Mensagens de sessão saem
// por ela; template/fora-24h continuam pelo número oficial. Sem atrelar outra caixa.
export default {
  props: {
    inbox: { type: Object, required: true },
  },
  data() {
    return {
      connection: null, // ConnectionInstance da perna sem-cobrança (se já criada)
      loading: true,
      connecting: false,
      showQR: false,
      qrImage: '',
      qrStatus: '',
      qrError: '',
      qrTimer: null,
      stateTimer: null,
      qrTick: 0,
      providers: [],
      providerId: '',
      // toggles de rota por tipo de mensagem (dentro das 24h)
      routes: { agent: true, bot: true, scheduled: true, campaign: false },
      savingRoutes: false,
    };
  },
  computed: {
    accountId() {
      return this.$route.params.accountId;
    },
    base() {
      return `/api/v1/accounts/${this.accountId}/kanban/connections`;
    },
    status() {
      return this.connection?.status || 'disconnected';
    },
    isConnected() {
      return this.status === 'connected';
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    this.stopQR();
    if (this.stateTimer) clearInterval(this.stateTimer);
  },
  methods: {
    async load() {
      try {
        const { data } = await axios.get(`${this.base}/by_inbox`, {
          params: { inbox_id: this.inbox.id },
        });
        this.connection = data.data;
      } catch (e) {
        this.connection = null;
      } finally {
        this.loading = false;
      }
      if (this.connection && !this.isConnected) this.startStatePolling();
      this.fetchRoutes();
      this.fetchProviders();
    },
    async fetchProviders() {
      try {
        const { data } = await axios.get(`${this.base}/providers`);
        this.providers = data.data || [];
        // default: provedor da conexão atual, senão o primeiro
        this.providerId =
          this.providers.find(p => p.provider_type === this.connection?.provider)?.id ||
          this.providers[0]?.id ||
          '';
      } catch (e) {
        this.providers = [];
      }
    },
    async fetchRoutes() {
      // busca as rotas DIRETO do backend (fresco do DB), sem depender do store
      try {
        const { data } = await axios.get(`${this.base}/hybrid_routes`, {
          params: { inbox_id: this.inbox.id },
        });
        const d = data.data || {};
        this.routes = {
          agent: d.agent !== false,
          bot: d.bot !== false,
          scheduled: d.scheduled !== false,
          campaign: d.campaign === true,
        };
      } catch (e) {
        /* mantém os padrões */
      }
    },
    async saveRoutes() {
      this.savingRoutes = true;
      try {
        await axios.patch(`${this.base}/hybrid_routes`, {
          inbox_id: this.inbox.id,
          hybrid_route_agent: this.routes.agent,
          hybrid_route_bot: this.routes.bot,
          hybrid_route_scheduled: this.routes.scheduled,
          hybrid_route_campaign: this.routes.campaign,
        });
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_HYBRID.ROUTES_SAVED'));
      } catch (e) {
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_HYBRID.SAVE_ERROR'));
      } finally {
        this.savingRoutes = false;
      }
    },
    startStatePolling() {
      if (this.stateTimer) clearInterval(this.stateTimer);
      this.stateTimer = setInterval(this.refreshState, 5000);
    },
    async refreshState() {
      if (!this.connection) return;
      try {
        const { data } = await axios.get(
          `${this.base}/${this.connection.instance_name}/state`
        );
        if (data.status) this.connection = { ...this.connection, status: data.status };
        if (this.isConnected && this.stateTimer) {
          clearInterval(this.stateTimer);
          this.stateTimer = null;
        }
      } catch (e) {
        /* silencioso */
      }
    },
    // O AstraCall pode entregar o TEXTO do QR → renderiza no cliente.
    async normalizeQr(raw) {
      if (!raw) return '';
      if (raw.startsWith('data:')) return raw;
      if (/[:@]|wa\.me|linked_devices/.test(raw)) {
        try {
          return await QRCode.toDataURL(raw, { margin: 1, width: 256 });
        } catch (e) {
          return '';
        }
      }
      return `data:image/png;base64,${raw}`;
    },
    stopQR() {
      if (this.qrTimer) clearInterval(this.qrTimer);
      this.qrTimer = null;
    },
    async connect() {
      if (!this.providerId) {
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_HYBRID.PICK_PROVIDER'));
        return;
      }
      this.connecting = true;
      this.qrError = '';
      try {
        // cria/garante a sessão do provedor escolhido, amarrada a ESTA caixa Cloud
        const { data } = await axios.post(`${this.base}/hybrid_connect`, {
          inbox_id: this.inbox.id,
          provider_id: this.providerId,
        });
        this.connection = data.data;
        this.showQR = true;
        this.qrTick = 0;
        this.qrImage = await this.normalizeQr(data.qrcode);
        if (data.error) this.qrError = data.error;
        await this.refreshQR(); // 1º QR (pareia uma vez)
        // Poll de ESTADO a cada 4s (NÃO re-pareia); QR só é refeito devagar, senão
        // cada /pair invalida o QR antes de dar tempo de escanear.
        this.qrTimer = setInterval(this.pollConnect, 4000);
      } catch (e) {
        useAlert(
          e?.response?.data?.error || this.$t('INBOX_MGMT.WHATSAPP_HYBRID.SAVE_ERROR')
        );
      } finally {
        this.connecting = false;
      }
    },
    async pollConnect() {
      if (!this.connection) return;
      this.qrTick += 1;
      try {
        const { data } = await axios.get(
          `${this.base}/${this.connection.instance_name}/state`
        );
        const st = data.status || '';
        this.qrStatus = st;
        if (/(open|connected)/i.test(st)) {
          this.stopQR();
          this.showQR = false;
          await this.load();
          return;
        }
      } catch (e) {
        /* silencioso */
      }
      // WhatsApp expira o QR (~20s): refaz o QR a cada ~24s (6 ticks), não a cada 4s.
      if (this.qrTick % 6 === 0) await this.refreshQR();
    },
    async refreshQR() {
      if (!this.connection) return;
      try {
        const { data } = await axios.get(
          `${this.base}/${this.connection.instance_name}/qrcode`
        );
        if (data.error) this.qrError = data.error;
        this.qrImage = await this.normalizeQr(data.qrcode);
        this.qrStatus = data.status?.state || data.status || this.qrStatus;
        if (/(open|connected)/i.test(this.qrStatus)) {
          this.stopQR();
          this.showQR = false;
          await this.load();
        }
      } catch (e) {
        this.qrError =
          e?.response?.data?.error || this.$t('INBOX_MGMT.WHATSAPP_HYBRID.QR_LOADING');
      }
    },
    async disconnect() {
      if (!this.connection) return;
      try {
        await axios.post(`${this.base}/${this.connection.instance_name}/logout`);
        await this.load();
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_HYBRID.DISCONNECTED'));
      } catch (e) {
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_HYBRID.SAVE_ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-6 py-4">
    <div class="flex flex-col gap-2">
      <h4 class="text-base font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.WHATSAPP_HYBRID.TITLE') }}
      </h4>
      <p class="text-sm leading-relaxed text-n-slate-11">
        {{ $t('INBOX_MGMT.WHATSAPP_HYBRID.DESCRIPTION') }}
      </p>
    </div>

    <div v-if="!loading" class="flex flex-col gap-4">
      <!-- Seletor de provedor (só quando ainda não conectado) -->
      <div v-if="!isConnected" class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_HYBRID.PROVIDER_LABEL') }}
        </label>
        <p v-if="!providers.length" class="text-sm text-n-amber-11">
          {{ $t('INBOX_MGMT.WHATSAPP_HYBRID.NO_PROVIDERS') }}
        </p>
        <select
          v-else
          v-model="providerId"
          class="max-w-md text-sm border rounded-md py-2 px-3 border-n-weak bg-n-background outline-none focus:ring-2 focus:ring-woot-500"
        >
          <option v-for="p in providers" :key="p.id" :value="p.id">
            {{ p.name }} ({{ p.provider_type }})
          </option>
        </select>
      </div>

      <!-- Estado atual da perna sem-cobrança -->
      <div
        class="flex items-center gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <span
          class="inline-block rounded-full size-2.5 shrink-0"
          :class="isConnected ? 'bg-n-teal-9' : 'bg-n-slate-8'"
        />
        <div class="flex-1 min-w-0">
          <div class="font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.WHATSAPP_HYBRID.SECONDARY_LEG') }}
          </div>
          <p class="text-sm text-n-slate-10">
            {{
              isConnected
                ? $t('INBOX_MGMT.WHATSAPP_HYBRID.CONNECTED')
                : connection
                  ? $t('INBOX_MGMT.WHATSAPP_HYBRID.CONNECTING')
                  : $t('INBOX_MGMT.WHATSAPP_HYBRID.NOT_CONNECTED')
            }}
          </p>
        </div>
        <button
          v-if="!isConnected"
          type="button"
          :disabled="connecting"
          class="reset-base cursor-pointer rounded-md bg-woot-500 hover:bg-woot-600 disabled:opacity-60 text-white text-sm font-medium py-2 px-4"
          @click="connect"
        >
          {{
            connecting
              ? $t('INBOX_MGMT.WHATSAPP_HYBRID.CONNECTING')
              : $t('INBOX_MGMT.WHATSAPP_HYBRID.CONNECT')
          }}
        </button>
        <button
          v-else
          type="button"
          class="reset-base cursor-pointer rounded-md border border-n-weak hover:bg-n-alpha-1 text-n-slate-12 text-sm font-medium py-2 px-4"
          @click="disconnect"
        >
          {{ $t('INBOX_MGMT.WHATSAPP_HYBRID.DISCONNECT') }}
        </button>
      </div>

      <!-- QR pra ler DENTRO da caixa -->
      <div
        v-if="showQR"
        class="p-6 text-center border rounded-xl border-n-weak bg-n-solid-1"
      >
        <p class="mb-3 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WHATSAPP_HYBRID.QR_HELP') }}
        </p>
        <img
          v-if="qrImage"
          :src="qrImage"
          alt="QR"
          class="inline-block rounded-lg size-64"
        />
        <p v-else class="text-sm text-n-slate-10">
          {{ $t('INBOX_MGMT.WHATSAPP_HYBRID.QR_LOADING') }}
        </p>
        <p v-if="qrError" class="mt-2 text-xs text-n-ruby-11">{{ qrError }}</p>
        <p v-else-if="qrStatus" class="mt-2 text-xs text-n-slate-10">
          {{ qrStatus }}
        </p>
      </div>

      <!-- Rotas por tipo de mensagem (dentro das 24h) -->
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
        <div class="font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.WHATSAPP_HYBRID.ROUTES_TITLE') }}
        </div>
        <p class="mt-1 mb-3 text-sm text-n-slate-10">
          {{ $t('INBOX_MGMT.WHATSAPP_HYBRID.ROUTES_HINT') }}
        </p>
        <div class="flex flex-col gap-2">
          <label class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-12">
            <input v-model="routes.agent" type="checkbox" class="rounded border-n-slate-6 text-woot-500 focus:ring-woot-500" />
            {{ $t('INBOX_MGMT.WHATSAPP_HYBRID.ROUTE_AGENT') }}
          </label>
          <label class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-12">
            <input v-model="routes.bot" type="checkbox" class="rounded border-n-slate-6 text-woot-500 focus:ring-woot-500" />
            {{ $t('INBOX_MGMT.WHATSAPP_HYBRID.ROUTE_BOT') }}
          </label>
          <label class="flex items-center gap-2 text-sm cursor-pointer text-n-slate-12">
            <input v-model="routes.scheduled" type="checkbox" class="rounded border-n-slate-6 text-woot-500 focus:ring-woot-500" />
            {{ $t('INBOX_MGMT.WHATSAPP_HYBRID.ROUTE_SCHEDULED') }}
          </label>
          <label class="flex items-start gap-2 text-sm cursor-pointer text-n-slate-12">
            <input v-model="routes.campaign" type="checkbox" class="mt-0.5 rounded border-n-slate-6 text-woot-500 focus:ring-woot-500" />
            <span class="flex flex-col">
              {{ $t('INBOX_MGMT.WHATSAPP_HYBRID.ROUTE_CAMPAIGN') }}
              <span class="text-xs text-n-amber-11">{{ $t('INBOX_MGMT.WHATSAPP_HYBRID.ROUTE_CAMPAIGN_WARN') }}</span>
            </span>
          </label>
        </div>
        <p class="mt-3 text-xs text-n-slate-10">
          {{ $t('INBOX_MGMT.WHATSAPP_HYBRID.ROUTES_FIXED') }}
        </p>
        <button
          type="button"
          :disabled="savingRoutes"
          class="reset-base cursor-pointer rounded-md bg-woot-500 hover:bg-woot-600 disabled:opacity-60 text-white text-sm font-medium py-2 px-4 mt-3"
          @click="saveRoutes"
        >
          {{ savingRoutes ? $t('INBOX_MGMT.WHATSAPP_HYBRID.CONNECTING') : $t('INBOX_MGMT.WHATSAPP_HYBRID.ROUTES_SAVE') }}
        </button>
      </div>
    </div>
  </div>
</template>
